import { buyerCreatesOrder, marketPendingOrderDetail } from "@/apis/market";
import { useConfigContext } from "@/app";
import { DialogLocal } from "@/components/Dialog";
import { StateReducer, useCreateReducer } from "@/libs/reducer";
import { useCryptoMerchant } from "@/stores/CryptoTradingStore";
import { usePaymentsStore } from "@/stores/payments";
import { useUserInfoStore } from "@/stores/userInfo";
import { MarketPendingOrderDetail } from "@/types/Api/Market/PendingOrder/marketPendingOrderDetail";
import { toMoney } from "@/utils/tool";
import { Toast } from "antd-mobile";
import { OrderType } from "@/constants";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
/**
 * 使用 Turn Interface to Object 插件去直接成 pagestate
 */
interface PageState {
  amount: string;
  buyerPaymentTypeId?: string;
  paymentType: number;
}

const pagestate = {
  amount: "",
  buyerPaymentTypeId: "",
  paymentType: 0,
};

type ActionType = "update-payments" | "update-amount";

const stateReducer: StateReducer<PageState, ActionType> = (state, action) => {
  switch (action.type) {
    case "update-payments":
      return Object.assign({}, state, {
        buyerPaymentTypeId: action.buyerPaymentTypeId,
        paymentType: action.paymentType,
      });
    case "update-amount":
      return Object.assign({}, state, {
        amount: action.amount,
      });

    default:
      return state;
  }
};

export const useSetup = () => {
  const params = useParams();
  const navigate = useNavigate();
  const cryptoMerchantStore = useCryptoMerchant();
  const { getSourcePath, config } = useConfigContext();
  const [userinfo] = useUserInfoStore()((state) => [state.userInfo]);
  const [paymentList] = usePaymentsStore((state) => [state.paymentList]);
  const [order, setOrder] = useState<MarketPendingOrderDetail["response"]>();
  const [state, dispatch] = useCreateReducer<PageState, ActionType>(
    stateReducer,
    pagestate,
    {}
  );

  const submitForm = useCallback(async () => {
    const response = await buyerCreatesOrder({
      pendingOrderId: params.orderId as string,
      amount: toMoney( order?.isSplit == OrderType.SEPARABLY ? state.amount : order?.validAmount || ""),
      buyerPaymentTypeId: String(state.buyerPaymentTypeId || ""),
      paymentType: String(state.paymentType),
    },{sellerType:cryptoMerchantStore.BuyerApiPath});
    if (response.ok) {
      // TODO: 跳转至购买者交易界面
      navigate(`/buy_trading/${response.data?.orderId}?m=${cryptoMerchantStore.isCryptoMerchant ?1:0}`, { replace: true });
    } else {
      Toast.show({
        icon: "fail",
        content: response.msg,
      });
    }
  }, [
    navigate,
    params.orderId,
    state.amount,
    state.buyerPaymentTypeId,
    state.paymentType,
    order?.isSplit,
    order?.validAmount,
  ]);

  const getOrderInfo = useCallback(async () => {
    const response = await marketPendingOrderDetail({
      pendingOrderId: Number(params.orderId),
    },{sellerType: cryptoMerchantStore.BuyerApiPath});
    if (response.ok) {
      if (paymentList.length === 0) {
        await DialogLocal({
          title: "请绑定支付账号",
          content:
            "您还未绑定该支付方式的支付账号，请绑定对应支付账号后再选择该支付方式",
          confirmText: "去绑定",
          cancelText: "取消",
          onConfirm() {
            // TODO: 跳转至添加支付方式页面
            navigate("/payment", { replace: true });
            return Promise.resolve(true);
          },
          onCancel: () => {
            return Promise.resolve(true);
          },
        });
      }
      setOrder(response.data);
    } else {
      // TODO: 订单详情请求失败
      await DialogLocal({
        title: "应用错误",
        content:
          "获取订单信息失败",
        confirmText: "重试",
        cancelText: "取消",
        onConfirm() {
          getOrderInfo();
          return Promise.resolve(true);
        },
        onCancel: () => {
          return Promise.resolve(true);
        },
      });
    }
  }, [params.orderId, paymentList.length, navigate]);

  const submit = useCallback(() => {
    //可拆分需要输入金额
    if( order?.isSplit == OrderType.SEPARABLY ){
      if (!state.amount) return Toast.show("请输入购买数额");
      if (!/^\d+$/.test(state.amount)) return Toast.show("请输入正确的购买数量");
      if (Number(state.amount) < Number(order?.minBuyEach)) {
        return Toast.show(`购买数额不得小于最低可购买额${order?.minBuyEach}`);
      }
      if (Number(state.amount) > Number(order?.validAmount)) {
        return Toast.show(`购买数额不得大于最高可购买额${order?.validAmount}`);
      }
    }
    
    if (!state.paymentType) return Toast.show("请选择支付方式");
    if (!state.buyerPaymentTypeId) return Toast.show("请选择支付账号");
    submitForm();
  }, [
    order?.minBuyEach,
    order?.validAmount,
    state.amount,
    state.buyerPaymentTypeId,
    state.paymentType,
    submitForm,
  ]);

  useEffect(() => {
    getOrderInfo();
  }, [getOrderInfo]);

  return {
    state,
    dispatch,
    order: order as MarketPendingOrderDetail["response"],
    getSourcePath,
    userinfo,
    submit,
    showSellerInfo: (config?.orderCreditInfoLevel || 0) < userinfo.creditLevel
  };
};
